import React, { useEffect, useState } from 'react'

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Controller, FormContext, useForm } from 'react-hook-form'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'
import useStyles from './styles'
import schema from './schema'
import { SmtpFields } from './components'

const DialogForm = ({
  open,
  setOpen,
  integration,
  onEvent,
  showSegcoopSection,
  segcoopOptionDisabled,
}) => {
  const [loading, setLoading] = useState()
  const classes = useStyles()
  const snackbar = useSnackbar()
  const isEditing = !!integration
  const {
    SEGCOOP_KIND,
    SMTP_KIND,
    ACTIVE_STATUS,
    INACTIVE_STATUS,
    PLATFORM_OPTIONS,
  } = constants.externalPlatformIntegrations

  const availablePlatformOptions = PLATFORM_OPTIONS.filter(
    (option) => option.id !== SEGCOOP_KIND || showSegcoopSection,
  )
  const defaultPlatform =
    (!segcoopOptionDisabled && availablePlatformOptions[0]?.id) || SMTP_KIND

  const formMethods = useForm({
    validationSchema: schema({ isEditing }),
    defaultValues: {
      externalPlatform: defaultPlatform,
      externalAccount: '',
      externalKey: '',
      externalSecret: '',
      address: '',
      port: '',
      domain: '',
      authentication: 'login',
      throttlePerMinute: '',
      throttlePerHour: '',
      starttls: true,
      ssl: false,
      status: true,
      pemFile: null,
      removePemFile: false,
    },
  })
  const { handleSubmit, control, errors, reset, watch } = formMethods

  useEffect(() => {
    if (open) {
      const additionalInfo = integration?.additionalInfo || {}
      reset({
        externalPlatform: integration?.externalPlatform || defaultPlatform,
        externalAccount: integration?.externalAccount || '',
        externalKey: integration?.externalKey || '',
        externalSecret: integration?.externalSecret || '',
        address: additionalInfo.address || '',
        port: additionalInfo.port ? String(additionalInfo.port) : '',
        domain: additionalInfo.domain || '',
        authentication: additionalInfo.authentication || 'login',
        throttlePerMinute: additionalInfo.throttlePerMinute
          ? String(additionalInfo.throttlePerMinute)
          : '',
        throttlePerHour: additionalInfo.throttlePerHour
          ? String(additionalInfo.throttlePerHour)
          : '',
        starttls: additionalInfo.starttls ?? true,
        ssl: additionalInfo.ssl ?? false,
        status: integration ? integration.status === ACTIVE_STATUS : true,
        pemFile: null,
        removePemFile: false,
      })
    }
  }, [open, integration])

  const buildSmtpAdditionalInfo = (data) => ({
    address: data.address,
    port: Number(data.port),
    domain: data.domain,
    authentication: data.authentication,
    starttls: data.starttls,
    ssl: data.ssl,
    ...(data.throttlePerMinute
      ? { throttlePerMinute: Number(data.throttlePerMinute) }
      : {}),
    ...(data.throttlePerHour
      ? { throttlePerHour: Number(data.throttlePerHour) }
      : {}),
  })

  const ADDITIONAL_INFO_FORM_DATA_KEYS = {
    address: 'address',
    port: 'port',
    domain: 'domain',
    authentication: 'authentication',
    starttls: 'starttls',
    ssl: 'ssl',
    throttlePerMinute: 'throttle_per_minute',
    throttlePerHour: 'throttle_per_hour',
  }

  // Quando um novo pem_file é selecionado, o envio precisa ser multipart —
  // por isso os demais campos são replicados aqui usando a convenção de
  // parâmetros aninhados do Rails (external_platform_integration[chave]),
  // já que o FormData não passa pelo helper de snake_case do axios.
  const buildSmtpFormData = (data) => {
    const formData = new FormData()

    formData.append(
      'external_platform_integration[external_platform]',
      data.externalPlatform,
    )
    formData.append(
      'external_platform_integration[external_account]',
      data.externalAccount,
    )
    formData.append(
      'external_platform_integration[external_key]',
      data.externalKey,
    )
    if (data.externalSecret) {
      formData.append(
        'external_platform_integration[external_secret]',
        data.externalSecret,
      )
    }
    formData.append('external_platform_integration[pem_file]', data.pemFile)
    if (!isEditing) {
      formData.append(
        'external_platform_integration[status]',
        data.status ? ACTIVE_STATUS : INACTIVE_STATUS,
      )
    }

    const additionalInfo = buildSmtpAdditionalInfo(data)
    Object.entries(additionalInfo).forEach(([key, value]) => {
      const formKey = ADDITIONAL_INFO_FORM_DATA_KEYS[key] || key
      formData.append(
        `external_platform_integration[additional_info][${formKey}]`,
        value,
      )
    })

    return formData
  }

  const onSubmit = async (data) => {
    const isSmtpPlatform =
      (data.externalPlatform || integration?.externalPlatform) === SMTP_KIND
    const hasNewPemFile = isSmtpPlatform && !!data.pemFile

    const payload = {
      externalPlatformIntegration: {
        externalPlatform: data.externalPlatform,
        externalAccount: data.externalAccount,
        externalKey: data.externalKey,
        ...(data.externalSecret ? { externalSecret: data.externalSecret } : {}),
        ...(isSmtpPlatform
          ? { additionalInfo: buildSmtpAdditionalInfo(data) }
          : {}),
        ...(isSmtpPlatform && data.removePemFile
          ? { removePemFile: true }
          : {}),
        ...(isEditing
          ? {}
          : {
              status: data.status ? ACTIVE_STATUS : INACTIVE_STATUS,
            }),
      },
    }

    try {
      setLoading(true)
      if (isEditing) {
        await service.dponet.externalPlatformIntegrations.update({
          integrationId: integration.id,
          ...(hasNewPemFile ? { data: buildSmtpFormData(data) } : payload),
        })
        snackbar.open({
          message: 'Integração atualizada com sucesso!',
          variant: 'success',
        })
      } else {
        await service.dponet.externalPlatformIntegrations.create(
          hasNewPemFile ? { data: buildSmtpFormData(data) } : payload,
        )
        snackbar.open({
          message: 'Integração criada com sucesso!',
          variant: 'success',
        })
      }
      onEvent()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error || 'Ocorreu um erro ao salvar a integração.',
          false,
        ),
        variant: 'error',
      })
    } finally {
      setLoading(false)
      setOpen(false)
    }
  }

  const externalPlatform = watch('externalPlatform')
  const isSegcoop = externalPlatform === SEGCOOP_KIND
  const isSmtp = externalPlatform === SMTP_KIND

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormContext {...formMethods}>
          <DialogTitle>
            <Box>
              <Typography variant="h5">
                {isEditing ? 'Editar integração' : 'Nova integração'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Grid container spacing={2}>
              {!isEditing && (
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="externalPlatform"
                    defaultValue=""
                    as={
                      <TextField
                        select
                        fullWidth
                        label="Plataforma"
                        variant="outlined"
                        error={!!errors.externalPlatform}
                        helperText={errors?.externalPlatform?.message || ''}
                      >
                        {availablePlatformOptions.map((option) => (
                          <MenuItem
                            key={option.id}
                            value={option.id}
                            disabled={
                              option.id === SEGCOOP_KIND &&
                              segcoopOptionDisabled
                            }
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    }
                  />
                </Grid>
              )}
              {isSmtp && (
                <SmtpFields
                  control={control}
                  errors={errors}
                  isEditing={isEditing}
                  pemFileFilename={integration?.pemFileFilename}
                />
              )}
              {!isSegcoop && !isSmtp && (
                <>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="externalAccount"
                      defaultValue=""
                      as={
                        <TextField
                          fullWidth
                          label="ID da Conta"
                          variant="outlined"
                          error={!!errors.externalAccount}
                          helperText={errors?.externalAccount?.message || ''}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="externalKey"
                      defaultValue=""
                      as={
                        <TextField
                          fullWidth
                          label="ID da Integração"
                          variant="outlined"
                          error={!!errors.externalKey}
                          helperText={errors?.externalKey?.message || ''}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="externalSecret"
                      defaultValue=""
                      as={
                        <TextField
                          fullWidth
                          label="Chave Secreta"
                          variant="outlined"
                          error={!!errors.externalSecret}
                          helperText={errors?.externalSecret?.message || ''}
                        />
                      }
                    />
                  </Grid>
                </>
              )}
              {!isEditing && (
                <Grid item xs={12}>
                  <FormControlLabel
                    label="Ativo"
                    control={
                      <Controller
                        control={control}
                        name="status"
                        defaultValue={true}
                        valueName="checked"
                        as={<Switch color="primary" />}
                      />
                    }
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
        </FormContext>
        <DialogActions>
          <Box pl={2} pr={1}>
            <Button
              disabled={loading}
              type="button"
              variant="text"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
          </Box>
          <Button
            disabled={loading}
            variant="contained"
            type="submit"
            color="primary"
          >
            {isEditing ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default DialogForm
